/**
 * Log temporário de consistência Matriz Semanal × Planner Heijunka.
 * Remover após validação em produção.
 */

import type { LstCargo, LstTemplate } from '../types';
import { formatTimeHHmm } from './lstMatrixHourly';
import { isTemplateDueOnDate } from './lstSchedule';
import {
  findPlannerBlock,
  plannerDayFromDate,
  PLANNER_WEEKDAYS,
  type PlannerBlock,
} from './tplPlannerBlocks';
import { templateAppliesToUser } from '../utils/lstTemplateScope';

const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function formatIsoShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  return `${WEEKDAY_SHORT[dow]} ${d}`;
}

export interface PlannerMatrixDebugRow {
  id: string;
  nome: string;
  frequencia: string;
  diasMatriz: string;
  diasPlanner: string;
  horarioMatriz: string;
  horarioPlanner: string;
  divergente: boolean;
}

export function buildPlannerMatrixDebugRows(
  templates: LstTemplate[],
  weekDates: string[],
  cargo: LstCargo,
  userId: string,
  plannerBlocks: PlannerBlock[],
): PlannerMatrixDebugRow[] {
  const userTemplates = templates.filter((t) => templateAppliesToUser(t, cargo, userId));

  return userTemplates.map((t) => {
    const matrixDayIsos = weekDates.filter((iso) => {
      if (plannerDayFromDate(iso) === null) return false;
      return isTemplateDueOnDate(t, iso, weekDates);
    });
    const matrixDayLabels = matrixDayIsos.map(formatIsoShort).join(', ') || '—';

    const plannerDayIds = [
      ...new Set(
        plannerBlocks.filter((b) => b.templateId === t.id).map((b) => b.day),
      ),
    ].sort();
    const plannerDayLabels =
      plannerDayIds
        .map((id) => PLANNER_WEEKDAYS.find((w) => w.id === id)?.label ?? String(id))
        .join(', ') || '—';

    const block = findPlannerBlock(plannerBlocks, t.id);
    const horarioMatriz = formatTimeHHmm(t.scheduled_time) ?? '—';
    const horarioPlanner = block?.hour ?? '—';

    const matrixWeekdaySet = new Set(
      matrixDayIsos.map((iso) => {
        const [y, m, d] = iso.split('-').map(Number);
        const dow = new Date(y, m - 1, d).getDay();
        return dow === 0 ? 7 : dow;
      }),
    );
    const plannerWeekdaySet = new Set(
      plannerDayIds.map((id) => (id === 5 ? 5 : id)),
    );

    const diasMatch =
      matrixDayIsos.length === plannerDayIds.length &&
      matrixDayIsos.every((iso) => {
        const [y, m, d] = iso.split('-').map(Number);
        const dow = new Date(y, m - 1, d).getDay();
        const mon0 = dow === 0 ? 6 : dow - 1;
        if (mon0 >= 5) return true;
        return plannerWeekdaySet.has(mon0 + 1);
      });

    const divergente = !diasMatch || (horarioPlanner !== '—' && horarioMatriz !== horarioPlanner);

    return {
      id: t.id,
      nome: t.title,
      frequencia: t.frequency,
      diasMatriz: matrixDayLabels,
      diasPlanner: plannerDayLabels,
      horarioMatriz,
      horarioPlanner,
      divergente,
    };
  });
}

export function logPlannerMatrixConsistency(
  templates: LstTemplate[],
  weekDates: string[],
  cargo: LstCargo,
  userId: string,
  plannerBlocks: PlannerBlock[],
): void {
  const rows = buildPlannerMatrixDebugRows(templates, weekDates, cargo, userId, plannerBlocks);
  const divergentes = rows.filter((r) => r.divergente);

  console.group('[TPL Debug] Matriz Semanal × Planner Heijunka');
  console.table(
    rows.map(({ id, nome, frequencia, diasMatriz, diasPlanner, horarioMatriz, horarioPlanner, divergente }) => ({
      ID: id,
      Nome: nome,
      Frequência: frequencia,
      'Dias Matriz': diasMatriz,
      'Dias Planner': diasPlanner,
      'Horário Matriz': horarioMatriz,
      'Horário Planner': horarioPlanner,
      Divergente: divergente ? 'SIM' : 'ok',
    })),
  );
  if (divergentes.length > 0) {
    console.warn(`${divergentes.length} rotina(s) com divergência:`, divergentes.map((r) => r.nome));
  } else {
    console.info('Matriz e Planner consistentes para a semana.');
  }
  console.groupEnd();
}
